<template>
  <div class="product-variant-slider">
    <fn-slider
      ref="slider"
      :options="{
        pagination: false,
        navigation: false,
        watchOverflow: false,
        slideToClickedSlide: true,
        slidesPerView: slidesPerViewS,
        initialSlide,
        slidesOffsetBefore: 0,
        freeMode: true,
        freeModeMomentumRatio: 0.5,
        spaceBetween: 10,
        breakpointsBase: 'container',
        breakpoints: {
          476: {
            slidesPerView: slidesPerViewL,
          },
        },
      }"
    >
      <fn-slide
        v-for="(option, index) in options"
        :key="option.label"
        :to="option.url"
        :class="{
          'pvs--selected': value === option.url,
          'pvs--disabled': !!notInStockErrorI18n(option),
        }"
        @focus="handleSlideFocus(index)"
      >
        <fn-product-variant-slide
          :data="option"
          :not-in-stock-error-i18n="notInStockErrorI18n(option)"
          @input="(valueNew) => $emit('input', valueNew)"
        />
      </fn-slide>
    </fn-slider>
  </div>
</template>

<script>
import { getNotInStockErrorI18n } from '@/utils/stock';

export default defineComponent({
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
  },
  emits: ['input'],
  data: () => ({
    slidesPerViewS: 1.5,
    slidesPerViewL: 2.5,
  }),
  computed: {
    ...mapState(useUserStore, {
      isAuthenticated: 'getIsAuthenticated',
      hasFriendsBenefit: 'getHasFriendsBenefit',
    }),
    activeSlide() {
      for (let i = 0; i < this.options.length; i++) {
        if (this.value === this.options[i].url) {
          return i;
        }
      }

      return null;
    },
    initialSlide() {
      return this.activeSlide ?? 0;
    },
  },
  methods: {
    notInStockErrorI18n(stockLevelStatus) {
      return getNotInStockErrorI18n(stockLevelStatus);
    },
    handleSlideFocus(index) {
      // Find the swiper-container element and access its swiper instance
      const swiperContainer = this.$refs.slider?.$el?.querySelector('.swiper-container');
      const swiper = swiperContainer?.swiper;

      if (swiper) {
        swiper.slideTo(index);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
// Define enough rows for rendering, plus a buffer
$row-count: 10;
$border-hover-transition-speed: 0.1s;

:deep(.swiper-container) {
  container-type: inline-size;
}

:deep(.slider .swiper-wrapper) {
  --slide-count: v-bind('options.length');
  --slide-per-view-s: v-bind(slidesPerViewS);
  --slide-per-view-l: v-bind(slidesPerViewL);

  // CSS vars via inline styles on the component root element
  --column-count: var(--slide-per-view-s);

  // Calculate slide width to be in sync with swiper
  --slides-width: calc(100% / min(var(--slide-count), var(--column-count)));

  display: grid;
  grid-template-columns: repeat(var(--slide-count), minmax(var(--slides-width), 1fr));
  grid-auto-flow: column;
  grid-auto-rows: auto;
  grid-column-gap: 10px;

  // Set wrapper width to fix overflowing/overlapping slides
  width: calc(100% - (10px * (var(--column-count) - 1)));
  margin: 0 auto 0 0;

  @container (min-width: 476px) {
    --column-count: var(--slide-per-view-l);
  }

  a {
    color: $color-action-text-neutral-default-base;
  }

  .swiper-slide {
    position: relative;
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span $row-count;
    box-shadow: 0 0 0 map-get($border-width, 'xs') $color-action-border-neutral-default-base;
    border-radius: map-get($border-radiuses, 'xl');
    cursor: pointer;

    // !important used to override inline styles
    width: auto !important;
    margin: (map-get($border-width, 'm')) (map-get($border-width, 'm')) (map-get($border-width, 'm') + 1) !important;

    transition: box-shadow $border-hover-transition-speed;

    &:not(.pvs--disabled) {
      &:hover {
        box-shadow: 0 0 0 map-get($border-width, 'm') $color-action-border-primary-default-hover;
      }

      &.pvs--selected {
        box-shadow: 0 0 0 map-get($border-width, 'm') $color-action-border-primary-default-base;
      }
    }

    &:focus-visible {
      @include focus-outline-default;
      outline-offset: 0;
      outline-width: 3px;
      border-radius: 12px;

      &:after {
        content: none;
      }

      &.pvs--selected {
        outline-offset: -2px;
      }
    }

    &.pvs--disabled {
      box-shadow: 0 0 0 map-get($border-width, 'xs') $color-action-border-primary-default-disabled;
      background: $color-action-background-neutral-high-disabled;
      color: $color-action-text-neutral-low-disabled;
      cursor: default;

      :deep(.p-regular-price-value),
      :deep(.p-per-unit) {
        color: $color-action-text-neutral-low-disabled !important;
      }
    }
  }
}
</style>
